import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Box, Typography, Paper } from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

export default function SignInPage() {
  const { signInWithGoogle, currentUser, loading } = useAuth();
  const navigate = useNavigate();

  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('SignInPage:', { 
      currentUser: currentUser ? 'User exists' : 'No user', 
      loading 
    });
  }

  const handleSignIn = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('SignInPage: Starting sign-in process...');
      }
      await signInWithGoogle();
      if (process.env.NODE_ENV === 'development') {
        console.log('SignInPage: Sign-in successful, navigating to dashboard...');
      }
      // Navigate to dashboard after successful sign-in
      navigate('/dashboard');
    } catch (error) {
      console.error('SignInPage: Sign in error:', error);
      // You can add error handling here (toast notifications, etc.)
    }
  }, [signInWithGoogle, navigate]);

  // If user is already signed in, redirect to dashboard
  useEffect(() => {
    if (currentUser && !loading) {
      if (process.env.NODE_ENV === 'development') {
        console.log('SignInPage: User already signed in, redirecting to dashboard');
      }
      navigate('/dashboard');
    }
  }, [currentUser, loading, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Typography variant="h6">Checking authentication...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 400,
          width: '100%'
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Party Planner
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Sign in to start planning your next party
        </Typography>
        
        <Button
          variant="contained"
          size="large"
          startIcon={<GoogleIcon />}
          onClick={handleSignIn}
          sx={{
            backgroundColor: '#4285f4',
            '&:hover': {
              backgroundColor: '#357ae8'
            },
            px: 4,
            py: 1.5
          }}
        >
          Sign in with Google
        </Button>

        {/* Debug Info - Only in development */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f0f0f0', borderRadius: 1, width: '100%' }}>
            <Typography variant="caption" component="div">
              Debug: currentUser = {currentUser ? 'Yes' : 'No'}, loading = {loading.toString()}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
